// Monthly IDM Sales Report (issue #25) — replaces the spreadsheet Kalpana
// maintained by hand. Pick a month, check the figures on screen, download the
// three-sheet workbook.
//
// The on-screen preview matters as much as the download: she cross-checks
// against her own records, and being able to see a day's numbers without
// opening Excel is what makes that quick.

import { useEffect, useState } from 'react';
import { salesReportApi } from '../api/services';
import toast from 'react-hot-toast';
import { Download, Loader2 } from 'lucide-react';

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const money = (n) => `$${Number(n || 0).toLocaleString('en-US', {
  minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function SalesReport() {
  const now = new Date();
  // Default to last month — the report is generated after a month closes.
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const [year, setYear] = useState(prev.getFullYear());
  const [month, setMonth] = useState(prev.getMonth() + 1);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    setLoading(true);
    salesReportApi.get({ year, month })
      .then((res) => setReport(res.data))
      .catch((err) => toast.error(err.response?.data?.error || 'Failed to load report'))
      .finally(() => setLoading(false));
  }, [year, month]);

  async function download() {
    setDownloading(true);
    try {
      await salesReportApi.download({ year, month });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Download failed');
    } finally {
      setDownloading(false);
    }
  }

  const couriers = report?.couriers || [];
  const t = report?.totals;
  const years = Array.from({ length: 4 }, (_, i) => now.getFullYear() - i);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Sales report</h1>
          <p className="text-sm text-gray-500 mt-1">
            Grouped by the original MBO file date. Counts are <strong>parcels</strong> —
            a split order shipped as two parcels counts twice — while the order
            value is counted once. Cancelled parcels and refunds excluded.
          </p>
          {/* Kalpana/Pratiksha, 9 Aug: refunds are sometimes processed by email
              or in MBO, which DMS never sees. Say so on the page — a sales
              figure presented without this reads as exact when it isn't. */}
          <p className="text-xs text-amber-700 mt-1">
            Refunds processed outside DMS (by email or in MBO) are not deducted,
            so sales may be slightly overstated.
          </p>
        </div>
        <button
          onClick={download}
          disabled={downloading || !report?.days?.length}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
        >
          {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
          Download XLSX
        </button>
      </div>

      <div className="flex items-center gap-2 mb-5">
        <select
          value={month} onChange={(e) => setMonth(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          {MONTHS.map((n, i) => <option key={n} value={i + 1}>{n}</option>)}
        </select>
        <select
          value={year} onChange={(e) => setYear(Number(e.target.value))}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
        >
          {years.map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="p-8 text-center text-gray-500">
          <Loader2 className="w-5 h-5 animate-spin inline" />
        </div>
      ) : !report?.days?.length ? (
        <div className="p-8 text-center text-gray-500 bg-white rounded-xl border border-gray-200">
          No orders for {MONTHS[month - 1]} {year}.
        </div>
      ) : (
        <>
          <dl className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
            {[
              ['Total sales', money(t.total_sales)],
              ['Total parcels', t.total_orders.toLocaleString()],
              ['Reship parcels', t.reship_orders.toLocaleString()],
              ['Days', report.days.length],
            ].map(([k, v]) => (
              <div key={k} className="bg-white rounded-lg border border-gray-200 p-3">
                <div className="text-xs uppercase text-gray-500">{k}</div>
                <div className="text-lg font-semibold">{v}</div>
              </div>
            ))}
          </dl>

          <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                <tr>
                  <th className="px-4 py-2 text-left">Date</th>
                  {couriers.map((c) => <th key={c} className="px-4 py-2 text-right">{c} value</th>)}
                  <th className="px-4 py-2 text-right font-semibold">Total value</th>
                  {couriers.map((c) => <th key={c} className="px-4 py-2 text-right">{c}</th>)}
                  <th className="px-4 py-2 text-right font-semibold">Parcels</th>
                  <th className="px-4 py-2 text-right">Reship</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {report.days.map((d) => (
                  <tr key={d.date} className="hover:bg-gray-50">
                    <td className="px-4 py-2 font-mono text-xs">{d.date}</td>
                    {couriers.map((c) => (
                      <td key={c} className="px-4 py-2 text-right text-gray-600">
                        {d[`sales_${c}`] ? money(d[`sales_${c}`]) : '—'}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right font-medium">{money(d.total_sales)}</td>
                    {couriers.map((c) => (
                      <td key={c} className="px-4 py-2 text-right text-gray-600">
                        {d[`orders_${c}`] || '—'}
                      </td>
                    ))}
                    <td className="px-4 py-2 text-right font-medium">{d.total_orders}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{d.reship_orders || '—'}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="bg-gray-50 font-semibold">
                <tr>
                  <td className="px-4 py-2">TOTAL</td>
                  {couriers.map((c) => (
                    <td key={c} className="px-4 py-2 text-right">{money(t[`sales_${c}`])}</td>
                  ))}
                  <td className="px-4 py-2 text-right">{money(t.total_sales)}</td>
                  {couriers.map((c) => (
                    <td key={c} className="px-4 py-2 text-right">{t[`orders_${c}`]}</td>
                  ))}
                  <td className="px-4 py-2 text-right">{t.total_orders}</td>
                  <td className="px-4 py-2 text-right">{t.reship_orders}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
